import jsPDF from 'jspdf';
import {
  PAPER_PROPERTIES,
  PAPER_TYPE,
  BINDING_SPINE_OFFSET,
  BINDING_TYPE,
  BOOK_COVER_DIMENSIONS,
  BOOK_SIZE,
  MIN_PAGES,
  type PaperType,
  type BindingType,
} from '../../../../../constants/bookFormFactor';

// Margin around the entire cover (inches) — the grey "avoid" zone
const BLEED_MARGIN = 0.25;
// Inner safe margin inside each section (inches) — content must stay within
const SAFE_MARGIN = 0.35;

/**
 * Calculate spine width in inches.
 * Paperback / Round Binding:  ((pages / 2) * GSM * BULK / 1000) + 0.6  mm
 * Hardback:                   ((pages / 2) * GSM * BULK / 1000) + 2     mm
 */
export const calculateSpineWidth = (
  pageCount: number,
  paperType: string,
  bindingType: string,
): number => {
  const paper = PAPER_PROPERTIES[paperType as PaperType] || PAPER_PROPERTIES[PAPER_TYPE.CREAM];
  const offset = BINDING_SPINE_OFFSET[bindingType as BindingType] ?? 0.6;
  const spineWidthMm = ((pageCount / 2) * paper.gsm * paper.bulk / 1000) + offset;
  return Math.round((spineWidthMm / 25.4) * 100) / 100;
};

/**
 * Estimate total page count from sections content.
 * ~2000 characters per page heuristic.
 */
export const estimatePageCount = (sections: Array<{ content?: string }> | undefined): number => {
  if (!sections || sections.length === 0) return 100;
  const CHARS_PER_PAGE = 2000;
  let totalChars = 0;
  for (const section of sections) {
    if (section.content) {
      totalChars += section.content.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim().length;
    }
  }
  const estimated = Math.max(10, Math.ceil(totalChars / CHARS_PER_PAGE));
  return estimated % 2 === 0 ? estimated : estimated + 1;
};

/**
 * Validate minimum page requirement for the given binding and paper type.
 * Returns error message string or null if valid.
 */
export const validateCoverRequirements = (
  bindingType: string,
  pageCount: number,
  paperType?: string,
): string | null => {
  const paperRow = MIN_PAGES[bindingType];
  if (!paperRow) return null;
  const minPages = paperRow[(paperType as PaperType) ?? PAPER_TYPE.CREAM] ?? paperRow[PAPER_TYPE.CREAM];
  if (pageCount < minPages) {
    return `${bindingType} requires a minimum of ${minPages} pages for ${paperType || 'this paper type'}. Your book currently has approximately ${pageCount} pages.`;
  }
  return null;
};

interface GenerateCoverTemplateParams {
  bookSize: string;
  paperType: string;
  bindingType: string;
  sections?: Array<{ content?: string }>;
  pageCount?: number;
}

/**
 * Generate a cover template PDF matching the IndiePress reference layout:
 * Grey bleed margins, safe areas with rounded rect borders, ISBN placeholder,
 * logo placeholders, and instructional text.
 */
export const generateCoverTemplate = ({
  bookSize,
  paperType,
  bindingType,
  sections,
  pageCount: providedPageCount,
}: GenerateCoverTemplateParams): void => {
  const pageCount = providedPageCount || estimatePageCount(sections);

  const dims = BOOK_COVER_DIMENSIONS[bookSize as keyof typeof BOOK_COVER_DIMENSIONS]
    || BOOK_COVER_DIMENSIONS[BOOK_SIZE['5.5x8.5']];
  const coverW = dims.width;
  const coverH = dims.height;
  const spineW = calculateSpineWidth(pageCount, paperType, bindingType);
  const totalW = Math.round((coverW + spineW + coverW) * 100) / 100;

  // Full page includes bleed margins on all sides
  const pageW = totalW + 2 * BLEED_MARGIN;
  const pageH = coverH + 2 * BLEED_MARGIN;

  const doc = new jsPDF({ orientation: 'landscape', unit: 'in', format: [pageW, pageH] });

  const M = BLEED_MARGIN; // shorthand

  // === 1. Grey bleed margin background (entire page) ===
  doc.setFillColor('#E0E0E0');
  doc.rect(0, 0, pageW, pageH, 'F');

  // === 2. White cover area (inside margins) ===
  doc.setFillColor('#FFFFFF');
  doc.rect(M, M, totalW, coverH, 'F');

  // Section x positions (relative to margin origin)
  const backX = M;
  const spineX = M + coverW;
  const frontX = M + coverW + spineW;

  // === 3. Section divider lines (solid black) ===
  doc.setDrawColor('#333333');
  doc.setLineWidth(0.008);
  // Outer border of entire cover area
  doc.rect(M, M, totalW, coverH);
  // Spine separators
  doc.line(spineX, M, spineX, M + coverH);
  doc.line(frontX, M, frontX, M + coverH);

  // === 4. Safe area borders (inner rounded rects) ===
  doc.setDrawColor('#999999');
  doc.setLineWidth(0.005);
  const S = SAFE_MARGIN;
  // Back cover safe area
  doc.roundedRect(backX + S, M + S, coverW - 2 * S, coverH - 2 * S, 0.08, 0.08);
  // Spine safe area
  if (spineW > 0.5) {
    const spineInset = Math.min(S, spineW * 0.15);
    doc.roundedRect(spineX + spineInset, M + S, spineW - 2 * spineInset, coverH - 2 * S, 0.05, 0.05);
  }
  // Front cover safe area
  doc.roundedRect(frontX + S, M + S, coverW - 2 * S, coverH - 2 * S, 0.08, 0.08);

  // === 5. Grey margin labels ===
  doc.setTextColor('#888888');
  doc.setFontSize(7);
  doc.setFont('helvetica', 'normal');
  const marginText = 'No important elements here (this area will be trimmed)';
  let tw = doc.getTextWidth(marginText);
  // Top
  doc.text(marginText, (pageW - tw) / 2, M / 2 + 0.04);
  // Bottom
  doc.text(marginText, (pageW - tw) / 2, pageH - M / 2 + 0.04);
  // Left — align:'center' centers using pre-rotation horizontal coords, pushing text into the
  // white cover area. Use explicit y start so the text is centred vertically within the strip.
  doc.text(marginText, M / 2, (pageH + tw) / 2, { angle: 90 });
  // Right
  doc.text(marginText, pageW - M / 2, (pageH - tw) / 2, { angle: -90 });

  // === 6. Dimension labels (top, between margin and cover) ===
  doc.setTextColor('#000000');
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  const dimY = M + 0.18;

  // Back cover width
  let text = `${coverW} inches`;
  tw = doc.getTextWidth(text);
  doc.text(text, backX + (coverW - tw) / 2, dimY);
  // Dashes around it
  doc.setLineDashPattern([0.05, 0.05], 0);
  doc.setDrawColor('#000000');
  doc.setLineWidth(0.005);
  const dashY = dimY - 0.05;
  doc.line(backX + 0.1, dashY, backX + (coverW - tw) / 2 - 0.08, dashY);
  doc.line(backX + (coverW + tw) / 2 + 0.08, dashY, backX + coverW - 0.1, dashY);

  // Spine width — always show the calculated value
  text = `${spineW} inches`;
  tw = doc.getTextWidth(text);
  if (tw > spineW - 0.1) {
    // Text too wide for spine column — show abbreviated above with smaller font
    doc.setFontSize(7);
    text = `${spineW}"`;
    tw = doc.getTextWidth(text);
  }
  doc.text(text, spineX + (spineW - tw) / 2, dimY);
  doc.setFontSize(10); // restore
  // Dashes
  doc.line(spineX + 0.05, dashY, spineX + spineW - 0.05, dashY);

  // Front cover width
  text = `${coverW} inches`;
  tw = doc.getTextWidth(text);
  doc.text(text, frontX + (coverW - tw) / 2, dimY);
  doc.line(frontX + 0.1, dashY, frontX + (coverW - tw) / 2 - 0.08, dashY);
  doc.line(frontX + (coverW + tw) / 2 + 0.08, dashY, frontX + coverW - 0.1, dashY);

  doc.setLineDashPattern([], 0);

  // Height label (left side, rotated)
  text = `${coverH} inches`;
  doc.setFontSize(10);
  doc.text(text, M + 0.18, M + coverH / 2, { angle: 90, align: 'center' });

  // === 7. Section titles and instructional text ===
  const backCenterX = backX + coverW / 2;
  const frontCenterX = frontX + coverW / 2;
  const spineCenterX = spineX + spineW / 2;
  const centerY = M + coverH / 2;

  // BACK COVER
  doc.setTextColor('#000000');
  doc.setFontSize(22);
  doc.setFont('helvetica', 'bold');
  text = 'BACK COVER';
  tw = doc.getTextWidth(text);
  doc.text(text, backCenterX - tw / 2, centerY - 0.8);

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor('#555555');
  const backInstr1 = 'Please ensure that any content added on the';
  const backInstr2 = 'back cover should lie in the white area';
  doc.text(backInstr1, backCenterX, centerY - 0.3, { align: 'center' });
  doc.text(backInstr2, backCenterX, centerY - 0.1, { align: 'center' });

  // SPINE
  doc.setTextColor('#000000');
  if (spineW >= 0.8) {
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('SPINE', spineCenterX, centerY, { align: 'center' });
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor('#555555');
    const spineInstr = 'Please ensure that any content added on the spine should lie in the white area';
    // Wrap manually for narrow spine
    const maxW = spineW - 0.3;
    const words = spineInstr.split(' ');
    let lines: string[] = [];
    let current = '';
    for (const word of words) {
      const test = current ? current + ' ' + word : word;
      if (doc.getTextWidth(test) > maxW && current) {
        lines.push(current);
        current = word;
      } else {
        current = test;
      }
    }
    if (current) lines.push(current);
    lines.forEach((line, i) => {
      doc.text(line, spineCenterX, centerY - 1.2 + i * 0.12, { align: 'center' });
    });
  } else if (spineW >= 0.4) {
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('SPINE', spineCenterX, centerY, { align: 'center', angle: 90 });
  } else {
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.text('SPINE', spineCenterX, centerY, { align: 'center', angle: 90 });
  }

  // FRONT COVER
  doc.setTextColor('#000000');
  doc.setFontSize(22);
  doc.setFont('helvetica', 'bold');
  text = 'FRONT COVER';
  tw = doc.getTextWidth(text);
  doc.text(text, frontCenterX - tw / 2, centerY - 0.8);

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor('#555555');
  doc.text('Please ensure that the Title, Author\'s name &', frontCenterX, centerY - 0.3, { align: 'center' });
  doc.text('any images added should lie in this white area', frontCenterX, centerY - 0.1, { align: 'center' });

  // === 8. ISBN placeholder (bottom-right of back cover) — 2" x 1" ===
  const isbnW = 2;
  const isbnH = 1;
  const isbnX = backX + coverW - S - isbnW - 0.1;
  const isbnY = M + coverH - S - isbnH - 0.1;

  doc.setFillColor('#E8E8E8');
  doc.roundedRect(isbnX, isbnY, isbnW, isbnH, 0.05, 0.05, 'F');
  doc.setTextColor('#666666');
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.text('leave this area empty', isbnX + isbnW / 2, isbnY + isbnH / 2 - 0.05, { align: 'center' });
  doc.text('for ISBN', isbnX + isbnW / 2, isbnY + isbnH / 2 + 0.08, { align: 'center' });

  // === 9. Logo placeholder (bottom-right of front cover) ===
  const frontLogoW = 0.8;
  const frontLogoH = 0.6;
  const frontLogoX = frontX + coverW - S - frontLogoW - 0.1;
  const frontLogoY = M + coverH - S - frontLogoH - 0.1;

  doc.setFillColor('#E8E8E8');
  doc.roundedRect(frontLogoX, frontLogoY, frontLogoW, frontLogoH, 0.05, 0.05, 'F');
  doc.setTextColor('#666666');
  doc.setFontSize(7);
  doc.text('leave this area', frontLogoX + frontLogoW / 2, frontLogoY + frontLogoH / 2 - 0.04, { align: 'center' });
  doc.text('empty for logo', frontLogoX + frontLogoW / 2, frontLogoY + frontLogoH / 2 + 0.08, { align: 'center' });

  // === 10. Logo placeholder (bottom of spine) ===
  // Rules: min spine width for logo = 12mm (~0.472")
  //        logo size = spine_width / 2 (1:2 ratio), square, max 200mm x 200mm
  const SPINE_LOGO_MIN_SPINE_MM = 12;
  const SPINE_LOGO_MAX_MM = 200;
  const spineWidthMm = spineW * 25.4;

  if (spineWidthMm >= SPINE_LOGO_MIN_SPINE_MM) {
    const spineLogoMm = Math.min(spineWidthMm / 2, SPINE_LOGO_MAX_MM);
    const spineLogoSize = spineLogoMm / 25.4; // convert to inches, square
    const spineLogoX = spineX + (spineW - spineLogoSize) / 2;
    const spineLogoY = M + coverH - S - spineLogoSize - 0.1;

    doc.setFillColor('#E8E8E8');
    doc.roundedRect(spineLogoX, spineLogoY, spineLogoSize, spineLogoSize, 0.04, 0.04, 'F');
    doc.setTextColor('#666666');
    const logoFontSize = spineLogoSize > 0.4 ? 6 : 5;
    doc.setFontSize(logoFontSize);
    doc.text('leave this', spineLogoX + spineLogoSize / 2, spineLogoY + spineLogoSize / 2 - 0.04, { align: 'center' });
    doc.text('area empty', spineLogoX + spineLogoSize / 2, spineLogoY + spineLogoSize / 2 + 0.05, { align: 'center' });
    doc.text('for logo', spineLogoX + spineLogoSize / 2, spineLogoY + spineLogoSize / 2 + 0.14, { align: 'center' });
  }

  // Open in new tab
  const pdfBlob = doc.output('blob');
  const url = URL.createObjectURL(pdfBlob);
  window.open(url, '_blank');
  setTimeout(() => URL.revokeObjectURL(url), 30000);
};
